import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getRefinerFields } from '../Helpers/DatatableHelpers';
import fields from '../../../Generic/Appointments/Components/Updater/Field.constants';
import viewUpdates from '../../../Generic/Appointments/Components/Updater/ViewUpdate.constants';
import { 
	clearRefineData,
	setGroupUpdaterResultsDetails,
	clearGroupUpdaterData,
	getGroupUpdaterUpdates,
	clearRefineFilters
} from '../../../../../Redux/Actions/Contract.Teams.Actions';

const useTeamManagement = (appointeeType, handleClearTeamsUpdates, handleGetContractTeamsTemplate, handleGetContractTeams, saveGroupUpdater) => {
 
	const dispatch = useDispatch();	
	const [pageSize, setPageSize] = useState(10);
	const [year, updateYear] = useState('');
	const [isFiltering, setIsFiltering] = useState(true);
	const [filterOpen, setFilterOpen] = useState(true);	
	const [isYearChange, setIsYearChange] = useState(false);	
	const [refinerOpen, setRefinerOpen] = useState(false);
	const [updaterOpen, setUpdaterOpen] = useState(false);
	const [viewDataOpen, setViewDataOpen] = useState(false);	
	const [isRefineRemove, setIsRefineRemove] = useState(false);
	const [isRefineAllClear, setIsRefineAllClear] = useState(false);
	const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);
	const [inPreview, setInPreview] = useState(false);	
	const [previewUpdate, setPreviewUpdate] = useState([]);
	const [updatedIds, setUpdatedIds] = useState([]);
	const [failedIds, setFailedIds] = useState([]);
	const [displayAppointmentsName, setDisplayAppointmentsName] = useState('');
	const [isSave, setIsSave] = useState(false);
	
	const toggleFilter = useCallback(() => setFilterOpen(prev => !prev), []);
	const toggleRefiner = useCallback(() => setRefinerOpen(prev => !prev), []);
	const toggleUpdater = useCallback(() => setUpdaterOpen(prev => !prev), []);
	const toggleViewData = useCallback(() => setViewDataOpen(prev => !prev), []);
	const refinerFields = useMemo(() => getRefinerFields('teams'), ['teams']);

	const types = useSelector(state => state.contract_globals.appointment_types.selected);
	const selectedTypes = useSelector(state => state.teams.selectedTypes) || types;	
	const selectedTeamIds = useSelector(state => state.teams.selectedTeamIds) || [];
	const selectedTemplateId = useSelector(state => state.teams.selectedTemplateId || [])
	const selectedContractStatus = useSelector(state => state.teams.selectedContractStatus) || [];
	const selectedMarkingMethod = useSelector(state => state.teams.selectedMarkingMethod) || [];
	const inputTitle = useSelector(state => state.teams.inputTitle) || '';
	const inputMarkingTeamCode = useSelector(state => state.teams.inputMarkingTeamCode) || '';
	const isDataAPICallCompleted = useSelector(state => state.teams.isDataAPICallCompleted) || false;
	const teamsTemplateData = useSelector(state => state.teams.templateData) || [];
	const refineData = useSelector(state => state.teams.refineData) || [];	
	const groupUpdaterData = useSelector(state => state.teams.groupUpdaterData) || [];	
	const isGroupUpdaterFilter = useSelector(state => state.teams.isGroupUpdaterFilter) || false;
	
	const handleClearRefineData = () => dispatch(clearRefineData());
	const handleGroupUpdaterResultsDetails = (isGroupUpdaterFilter, groupUpdaterTypes, groupUpdaterTeamIds) => dispatch(setGroupUpdaterResultsDetails(isGroupUpdaterFilter, groupUpdaterTypes, groupUpdaterTeamIds));
	const handleClearGroupUpdaterData = () => dispatch(clearGroupUpdaterData());
	const handleGetGroupUpdaterUpdates = () => dispatch(getGroupUpdaterUpdates());	
	const handleClearRefineFilters = () => dispatch(clearRefineFilters());

	const startNewFilter = () => {
		setIsFiltering(true);
		setFilterOpen(true);
		setInPreview(false);
		setIsRefineRemove(false);	
		setIsRefineAllClear(false);	
		setRefinerOpen(false);
		setUpdaterOpen(false);
		setViewDataOpen(false);
		setPageSize(10);
		setDisplayAppointmentsName('');
		setIsSave(false);
	};

	const handleFilterClear = useCallback(() => {
        setViewDataOpen(false);
		setPageSize(10);		
		setDisplayAppointmentsName('');
    }, []);	

	const handleFilterSubmit = useCallback(async(selectedYear, selectedFilteredTypes, selectedFilteredTeamIds) => {
		const selectedTypes = selectedFilteredTypes ? selectedFilteredTypes : types;
		const selectedTeamIds = selectedFilteredTeamIds ? selectedFilteredTeamIds : [];
		setIsFiltering(false);
		setFilterOpen(false);
		setRefinerOpen(false);
		setViewDataOpen(true);
		setIsYearChange(false);

		let query = { sortOrder: 'asc', sortField: 'title', page: 1, sizePerPage: 10, searchText: '', flagExportData: false };
		const updated = [];
		const exportData = false;
		handleClearTeamsUpdates();		
        if (!isTemplateLoaded) {
			handleGetContractTeamsTemplate(appointeeType);
			setIsTemplateLoaded(true);
        }
		handleGetContractTeams(selectedTypes, selectedYear, query, updated, exportData, selectedTeamIds);
		setDisplayAppointmentsName('Filtered');
	}, [handleGetContractTeams]);

	useEffect(() => {
		setViewDataOpen(false);
		setIsYearChange(true);
    }, [year]);

	useEffect(() => {
		if (refinerOpen && refineData.length == 0) {
			//_fetch all data to fill refinement fields.
			getRefineData();
		}
    }, [refinerOpen]);

	const getRefineData = () => {	
		const exportData = true, isRefine = true;
		resetDataTable(exportData, isRefine);
	};

	let refineSelectors = [];
    function onRefinementSelectorsChange(selectors, blankValue) {
		if(refineData.length > 0 && selectors && selectors.length > 0) 
		{
			//_setting default value of "Not Set".						
			const notSet = blankValue;				
			let templateId = [];
			let contractStatus = [];	
			let markingMethod = [];
			let title = '';
			let markingTeamCode = '';	
			let allValidSelection = true;	

			selectors.forEach(item => {
				const selectedItem = item.field;				
				const data = item.value || [];
				let isNotSetAdded = false;

				if(item.isValid) {
					switch (selectedItem) {
					case 'templateName':
						data.forEach(x => {
							if(x == notSet && !isNotSetAdded) {
								templateId.push('0');
								isNotSetAdded = true;
							}
							else 
							{
								const itemArray = x.split(' ');
								const itemId = itemArray[itemArray.length - 1];
								templateId.push(itemId);
							}
						});
						break;
					case 'displayStatus':
						data.forEach(x => { 
							if(x == notSet && !isNotSetAdded) {
								contractStatus.push('0');
								isNotSetAdded = true;
							}
							else 
							{								
								contractStatus.push(x);
							}
						});
						break;
					case 'markingMethodCurrentYear':
						data.forEach(x => {
							if(x == notSet && !isNotSetAdded) 
							{
								markingMethod.push('0');
								isNotSetAdded = true;
							}
							else 
							{
								markingMethod.push(x);
							}
						});
						break;
					case 'title':
						title = data || '';
						break;
					case 'markingTeamCode':
						markingTeamCode = data || '';
						break;
					default:
						return;
					}
				}
				else
				{
					allValidSelection = false;
				}
			});
			
			if(allValidSelection) {
				const exportData = false, isRefine = false;
				resetDataTable(exportData, isRefine, templateId, contractStatus, markingMethod, title, markingTeamCode);
				refineSelectors = [...selectors];
			}			
			setIsRefineRemove(false);
		}
		else {
			refineSelectors = [];
		}
	};

	function onRefinementSelectorsRemove() {
		setIsRefineRemove(true);		
	};

	useEffect(() => {
		if(isRefineRemove && refineSelectors.length == 0) {
			resetDataTable();
		}
    }, [isRefineRemove]);

	function onRefinementAllClear() {
		refineSelectors = [];
		setIsRefineAllClear(true);
	};

	useEffect(() => {
		if(isRefineAllClear) {
			setIsRefineAllClear(false);
			resetDataTable();
		}
    }, [isRefineAllClear]);

	function setPageDetails(pageSize) {
		const size = pageSize || 10
		setPageSize(size);
	};

	const getGroupUpdaterData = async () => {
		const exportData = true, isRefine = false, isGroupUpdater = true;
		await resetDataTable(exportData, isRefine, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode, isGroupUpdater);
	};
	
    const startNewUpdate = async () => {
		setIsSave(false);
		handleClearRefineFilters();
		handleClearGroupUpdaterData();
		handleClearRefineData();
		refineSelectors = [];		
        setIsRefineAllClear(true);	
		setRefinerOpen(false);
		setUpdaterOpen(false);
		setDisplayAppointmentsName('Filtered');
    };

	const resetDataTable = async(exportData = false, isRefine = false, templateId = [], contractStatus = [], markingMethod = [], title = '', markingTeamCode = '', isGroupUpdater = false, previewUpdate = [], isGroupUpdaterFilter = false) => {
		if(viewDataOpen) {
			const updated = [];			
			let types = selectedTypes, teamIds = selectedTeamIds;	
			let query = { sortOrder: 'asc', sortField: 'title', page: 1, sizePerPage: pageSize, searchText: '', flagExportData: exportData };

			await handleGetContractTeams(types, year, query, updated, exportData, teamIds, isRefine, templateId, contractStatus, markingMethod, title, markingTeamCode, isGroupUpdater, previewUpdate, isGroupUpdaterFilter);
		}
	};

	const updateDataTable = async (groupUpdaterSelections = [], displayName = '') => {	
		if(!isSave) {
			const exportData = false, isRefine = false;
			if (groupUpdaterSelections.length > 0) {
				const isGroupUpdater = false;
				let previewUpdate = [{ readyForApproval: null, markingMethod: null, templateName: null, id: null, contractReleaseDate: null, contractEndDate: null, emailAddress: null }];					

				groupUpdaterSelections.forEach(item => {
					const field = item.field;
					switch (field) {
						case fields.ReadyForApproval:
							previewUpdate[0].readyForApproval = item.value;
							break;
						case fields.MarkingMethod:
							previewUpdate[0].markingMethod = item.value;
							break;
						case fields.Template:
							const templateName = item.value;
							const template = teamsTemplateData.find(({ name }) => name == templateName);
							previewUpdate[0].templateName = templateName;
							previewUpdate[0].id = template.value;
							break;				
						case fields.ContractReleaseDate:
							previewUpdate[0].contractReleaseDate = item.value;
							break;
						case fields.ContractEndDate:
							previewUpdate[0].contractEndDate = item.value;
							break;
						case fields.TeamEmail:
							previewUpdate[0].emailAddress = item.value;
							break;
					}							
				});
				setPreviewUpdate(previewUpdate);
				setDisplayAppointmentsName(displayName); 

				await resetDataTable(exportData, isRefine, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode, isGroupUpdater, previewUpdate);
				getGroupUpdaterData();
			}
			else {
				await resetDataTable(exportData, isRefine, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode);
			}
		}
	}; 

    const saveData = async () => {
		let updates = [...groupUpdaterData] || [];
		let updatedIds = [], failedIds = [];

		if (previewUpdate && previewUpdate.length > 0) {
			if (updates.length == 0) {
				await getGroupUpdaterData();
			 	updates = [...groupUpdaterData];
			}

			const { readyForApproval = null, markingMethod = null, templateName = null, id = null, contractReleaseDate = null, contractEndDate = null, emailAddress = null } = previewUpdate[0];
			updates.forEach(element => {
				if (readyForApproval) {
					element.displayStatus = readyForApproval;
				}
				if (markingMethod) {
					element.markingMethodCurrentYear = markingMethod;	
				}
				if (templateName) {
					element.templateId = id;			
				}
				if (emailAddress) {
					element.emailAddress = emailAddress;
				}
				if(contractReleaseDate) {
					element.contractReleaseDate = contractReleaseDate;
					if(contractEndDate == null && element.contractReleaseDate && element.contractEndDate &&  element.contractReleaseDate < element.contractEndDate) {
						element.contractEndDate = null;
					}
				}
				else {
					element.contractReleaseDate = null;
				}
				if(contractEndDate) {
					element.contractEndDate = contractEndDate;
					if(contractReleaseDate == null && element.contractReleaseDate && element.contractEndDate &&  element.contractReleaseDate < element.contractEndDate) {
						element.contractReleaseDate = null;
					}
				}
				else {
					element.contractEndDate = null;
				}
			});
			
			await saveGroupUpdater(year, updates);
			handleGetGroupUpdaterUpdates();

			let isSaveData = false;
			const updatedData = [...groupUpdaterData] || [];
			updatedData.forEach(item => {
				if(item.isSave)	{
					updatedIds.push({ type: item.type, markingTeamId: item.markingTeamId });

					if(!isSaveData) {
						isSaveData = true;	
					}
				}
				else
				{
					failedIds.push({ type: item.type, markingTeamId: item.markingTeamId });
				}
			});
			setIsSave(isSaveData);

            const updatedAll = (updatedData.length == updatedIds.length);
			setUpdatedIds(updatedIds);
			setFailedIds(failedIds);	

            if (updatedAll) {
                setPreviewUpdate([]);
                await resetDataTable();
            }


			const result = { updatedAll, updatedIds, failedIds };
			return result;
		}
	}; 

	//_common method to display SAVED, FAILED and REFINED data.
	const displayData = (viewUpdatesType, displayName = '') => {
		const exportData = false, isRefine = false, isGroupUpdater = false, previewUpdate = [];
		let isGroupUpdaterFilter = true, groupUpdaterTypes = [], groupUpdaterTeamIds = [];
		switch (viewUpdatesType) {
			case viewUpdates.PartialSaved:
				updatedIds.forEach(item => {
					groupUpdaterTypes.push(item.type); 
					groupUpdaterTeamIds.push(item.markingTeamId); 
				});			
				break;
			case viewUpdates.Failed:
				failedIds.forEach(item => {
					groupUpdaterTypes.push(item.type); 
					groupUpdaterTeamIds.push(item.markingTeamId); 
				});			
				break;			
			case viewUpdates.Refined:
				isGroupUpdaterFilter = false;
				break;
		}	
		setDisplayAppointmentsName(displayName);							
		handleGroupUpdaterResultsDetails(isGroupUpdaterFilter, groupUpdaterTypes, groupUpdaterTeamIds);				
		resetDataTable(exportData, isRefine, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode, isGroupUpdater, previewUpdate, isGroupUpdaterFilter);
	};

    return { filterOpen, toggleFilter, isFiltering, setIsFiltering, year, updateYear, startNewFilter, handleFilterClear, handleFilterSubmit
    , refinerFields, refinerOpen, inPreview, refineData, onRefinementSelectorsChange, onRefinementSelectorsRemove, onRefinementAllClear, toggleRefiner
	, updaterOpen, toggleUpdater, setInPreview, startNewUpdate, updateDataTable, saveData, previewUpdate, isGroupUpdaterFilter, displayData, displayAppointmentsName
    , viewDataOpen, setViewDataOpen, toggleViewData, isYearChange, isDataAPICallCompleted, selectedTypes, selectedTeamIds
	, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode, setPageDetails };
};

useTeamManagement.propTypes = {
    appointeeType: PropTypes.array.isRequired,
	handleClearTeamsUpdates: PropTypes.func.isRequired,
	handleGetContractTeamsTemplate: PropTypes.func.isRequired,
    handleGetContractTeams: PropTypes.func.isRequired,
	saveGroupUpdater: PropTypes.func.isRequired
};

export default useTeamManagement;